import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  makeStyles, shorthands, tokens, mergeClasses,
  Body1, Caption1, Spinner, Button, Link, SearchBox,
} from '@fluentui/react-components';
import { OpenRegular, DataTrendingRegular } from '@fluentui/react-icons';
import { HeroProgressMetrics } from '../../components/common/HeroProgressMetrics';
import { useEpicTree, useMyProjects } from '../../api/hooks';
import { brandColors, brandFonts } from '../../theme/brandColors';
import type { EpicTreeNode } from '../../api/types';

/* EXECUTIVE-BRIEFING design (design-reference/executive-briefing-progress.html).
   Navy hero (kicker → epic H1 + status pill → status-breakdown bars + composite %),
   a 4-KPI tile row, then a 2-column detail grid: a stacked column chart of features
   by story status (left) and a panel with the epic description + drill-down feature/
   story cards (right). Data comes from rollup(epicTree) — preserved verbatim with the
   tested classify()/rollup() exports. */

// ── State classification (TESTED — keep classify/rollup signatures stable) ──
// Several helpers below are exported so FeatureProgressPage can reuse the exact
// same classification / flattening / completion logic (one source of truth).
export type Completion = 'closed' | 'resolved' | 'inProgress' | 'toDo' | 'removed';

export function classify(state: string): Completion {
  // `Implemented` is listed here defensively: it is a terminal state in the Story Point calcs
  // sheet but does not exist in our ADO process, and without it a story would silently score 0%.
  if (state === 'Closed' || state === 'Done' || state === 'Completed' || state === 'Implemented') return 'closed';
  if (state === 'Resolved') return 'resolved';
  if (state === 'Active' || state === 'In Progress' || state === 'Committed') return 'inProgress';
  if (isRemoved(state)) return 'removed';
  return 'toDo';
}
export function isDone(c: Completion): boolean { return c === 'closed' || c === 'resolved'; }
export function isRemoved(state: string): boolean {
  const s = state.trim().toLowerCase();
  return s.includes('remov') || s.includes('cut') || s.includes('abandon') || s.includes('cancel');
}

// ── Progress model (docs/Story Point calcs.xlsx) ──────────────────────────────
// A story earns PARTIAL credit for its state, weighted by its size:
//     Adjusted = StoryPoints × StatusWeight     Progress = ΣAdjusted / ΣStoryPoints
// The sheet's ladder is New 0 / Active .2 / Resolved .6 / Completed .8 / Implemented 1.
// `Completed` and `Implemented` do not exist in our ADO process (verified across every
// work item org-wide) — our terminal state is `Closed`, which the sheet's lookup omits
// altogether. Closed therefore maps to 1.0 (decision 2026-07-16). Weights hang off
// classify() so Done/Completed/In Progress/Committed keep resolving correctly.
export const STATUS_WEIGHT: Record<Completion, number> = {
  closed: 1,
  resolved: 0.6,
  inProgress: 0.2,
  toDo: 0,
  removed: 0, // excluded before weighting; never contributes
};
export function statusWeight(state: string): number { return STATUS_WEIGHT[classify(state)]; }

/** Which basis produced a percentage — story points, or an equal-weight story count. */
export type ProgressBasis = 'points' | 'count';
export interface ProgressResult {
  percent: number;
  basis: ProgressBasis;
  /** Stories carrying an estimate, and all non-removed stories — the coverage fraction. */
  pointedCount: number;
  storyCount: number;
  coverage: number;
}

// Story points only weight the headline when enough stories actually carry an estimate.
// Coverage is 74% org-wide and 0% in some projects, where weighting by points either
// divides by zero or scores delivered work at zero (Buildr: every Closed story is
// unpointed, so a points-only figure reads 0% against ~40% real delivery). Below this
// threshold we count stories equally instead and say so in the UI.
const POINTS_COVERAGE_MIN = 0.8;

/**
 * Raw ingredients of a progress figure, kept un-divided so they can be SUMMED across
 * epics/features before the percentage is taken. Averaging per-epic percentages would
 * weight a 2-story epic the same as a 50-story one. The server emits these same sums on
 * EpicSummary so the list pages and the drill-downs share one formula.
 */
export interface ProgressInput {
  /** Σ(points × weight) over pointed, non-removed stories. */
  adjustedPoints: number;
  /** Σ(points) over those same stories — the denominator when weighting by points. */
  totalPoints: number;
  /** Non-removed stories carrying an estimate. */
  pointedCount: number;
  /** All non-removed stories. */
  storyCount: number;
  /** Σ(weight) over all non-removed stories — the numerator for the count fallback. */
  adjustedCount: number;
}

const EMPTY_INPUT: ProgressInput = {
  adjustedPoints: 0, totalPoints: 0, pointedCount: 0, storyCount: 0, adjustedCount: 0,
};

/** Reduce a set of stories to the raw sums a progress figure is built from. */
export function progressInputOf(stories: readonly EpicTreeNode[]): ProgressInput {
  const active = stories.filter((s) => !isRemoved(s.state));
  // A 0-point story can't be weighted meaningfully, so it counts as unpointed.
  const pointed = active.filter((s) => (s.storyPoints ?? 0) > 0);
  return {
    adjustedPoints: pointed.reduce((sum, s) => sum + (s.storyPoints ?? 0) * statusWeight(s.state), 0),
    totalPoints: pointed.reduce((sum, s) => sum + (s.storyPoints ?? 0), 0),
    pointedCount: pointed.length,
    storyCount: active.length,
    adjustedCount: active.reduce((sum, s) => sum + statusWeight(s.state), 0),
  };
}

/** Combine inputs across epics/features — sum the ingredients, then divide once. */
export function sumProgressInputs(parts: readonly ProgressInput[]): ProgressInput {
  return parts.reduce((a, p) => ({
    adjustedPoints: a.adjustedPoints + p.adjustedPoints,
    totalPoints: a.totalPoints + p.totalPoints,
    pointedCount: a.pointedCount + p.pointedCount,
    storyCount: a.storyCount + p.storyCount,
    adjustedCount: a.adjustedCount + p.adjustedCount,
  }), EMPTY_INPUT);
}

/**
 * Turn raw sums into the headline percentage, choosing the basis. Falls back to
 * equal-weight counting when story-point coverage is too thin to trust, so the figure is
 * never NaN and never silently ignores unpointed delivered work.
 */
export function resolveProgress(i: ProgressInput): ProgressResult {
  if (i.storyCount === 0) {
    return { percent: 0, basis: 'count', pointedCount: 0, storyCount: 0, coverage: 0 };
  }
  const coverage = i.pointedCount / i.storyCount;
  const common = { pointedCount: i.pointedCount, storyCount: i.storyCount, coverage };
  return coverage >= POINTS_COVERAGE_MIN && i.totalPoints > 0
    ? { percent: Math.round((i.adjustedPoints / i.totalPoints) * 100), basis: 'points', ...common }
    : { percent: Math.round((i.adjustedCount / i.storyCount) * 100), basis: 'count', ...common };
}

/** Weighted completion for a set of stories, per the Story Point calcs model. */
export function weightedProgress(stories: readonly EpicTreeNode[]): ProgressResult {
  return resolveProgress(progressInputOf(stories));
}

/** Sub-line explaining which basis a progress figure used, for display under the number. */
export function progressBasisLabel(p: ProgressResult): string {
  return p.basis === 'points'
    ? `weighted by story points · ${p.pointedCount} of ${p.storyCount} stories estimated`
    : `by story count · only ${p.pointedCount} of ${p.storyCount} stories estimated`;
}

/** Format a weighted subtotal — whole numbers stay clean, partials show one decimal. */
export function formatPoints(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

export function flatten(node: EpicTreeNode, into: EpicTreeNode[] = []): EpicTreeNode[] {
  into.push(node);
  node.children?.forEach((c) => flatten(c, into));
  return into;
}

// Case-insensitive check for a semicolon-separated System.Tags value (guidance stores
// tags lowercase, but customer-created tags aren't guaranteed to be).
export function hasTag(node: EpicTreeNode, tag: string): boolean {
  return (node.tags ?? '').split(';').some((t) => t.trim().toLowerCase() === tag);
}

export interface DiscoveryStats { newCount: number; total: number; newPct: number; }

// New-scope rollup (Doc 29 — 29-DISCOVERY-SURFACED-WORK-ITEMS). Share of ALL NON-removed descendant
// work items (Features, Stories, Tasks, Bugs) tagged `discovery-new` — legitimate scope added AFTER
// the spec was signed off. Counted over every work-item type (not just User Stories) so it reflects
// the tagged items you actually see when drilling in, and matches the server-side epics-list count.
export function discoveryStats(root: EpicTreeNode): DiscoveryStats {
  const items = flatten(root).filter((n) => n.id !== root.id && !isRemoved(n.state));
  const total = items.length;
  const newCount = items.filter((n) => hasTag(n, 'discovery-new')).length;
  return { newCount, total, newPct: total > 0 ? Math.round((newCount / total) * 100) : 0 };
}

export interface QualityStats { openUat: number; openBug: number; }

// Quality signals (#13419): non-removed descendants (any type) that are NOT yet closed
// (Closed/Done/Completed — Resolved still counts as open, it awaits sign-off), split into items
// tagged `uat` and items of type Bug. The two are independent counts (a UAT-raised bug is both),
// and they use the same definitions as the server-side openUatCount / openBugCount on the epics
// list, so the list row and this page can never disagree.
export function qualityStats(root: EpicTreeNode): QualityStats {
  const open = flatten(root).filter(
    (n) => n.id !== root.id && !isRemoved(n.state) && classify(n.state) !== 'closed',
  );
  return {
    openUat: open.filter((n) => hasTag(n, 'uat')).length,
    openBug: open.filter((n) => n.workItemType === 'Bug').length,
  };
}

function featureCode(title: string): string {
  const m = title.match(/^([A-Z]+(?:-[A-Z0-9]+){1,3})(?=\b|[\s:_-])/);
  if (m) return m[1];
  return title.length > 18 ? `${title.slice(0, 16)}…` : title;
}

interface FeatureRow {
  id: number;
  code: string;
  name: string;
  customerTitle: string | null;
  state: string;
  description: string | null;
  closed: number;
  resolved: number;
  inProgress: number;
  toDo: number;
  removed: number;
  total: number;
  /** Raw progress ingredients, so a filtered set of features can be aggregated correctly. */
  progress: ProgressInput;
}

type StoryStateLabel = 'New' | 'Active' | 'Resolved' | 'Closed' | 'Removed';

interface RollupResult {
  features: FeatureRow[];
  storyStateCounts: { state: StoryStateLabel; count: number }[];
  typeCounts: { type: string; count: number }[];
  spDone: number;
  spClosed: number;
  spResolved: number;
  spRemaining: number;
  spTotal: number;
  usDone: number;
  usClosed: number;
  usResolved: number;
  usTotal: number;
  featuresDone: number;
  featuresTotal: number;
  // percentComplete == "Implemented" (Resolved + Closed) — kept for back-compat.
  percentComplete: number;
  percentResolved: number;
  percentClosed: number;
  /** Weighted completion (Story Point calcs model) — the headline progress figure. */
  progress: ProgressResult;
}

export function rollup(tree: EpicTreeNode): RollupResult {
  const all = flatten(tree);

  const features: FeatureRow[] = all
    .filter((n) => n.workItemType === 'Feature')
    .map((feat) => {
      const stories = flatten(feat).filter(
        (c) => c.workItemType === 'User Story' && c.id !== feat.id,
      );
      const counts = { closed: 0, resolved: 0, inProgress: 0, toDo: 0, removed: 0 };
      stories.forEach((s) => { counts[classify(s.state)]++; });
      return {
        id: feat.id,
        code: featureCode(feat.title),
        name: feat.title,
        customerTitle: feat.customerTitle,
        state: feat.state,
        description: feat.description,
        closed: counts.closed,
        resolved: counts.resolved,
        inProgress: counts.inProgress,
        toDo: counts.toDo,
        removed: counts.removed,
        total: stories.length,
        progress: progressInputOf(stories),
      };
    });

  const allStories = all.filter((n) => n.workItemType === 'User Story');
  const usCounts = { closed: 0, resolved: 0, inProgress: 0, toDo: 0, removed: 0 };
  allStories.forEach((s) => { usCounts[classify(s.state)]++; });

  const storyStateCounts = [
    { state: 'New' as const, count: usCounts.toDo },
    { state: 'Active' as const, count: usCounts.inProgress },
    { state: 'Resolved' as const, count: usCounts.resolved },
    { state: 'Closed' as const, count: usCounts.closed },
    { state: 'Removed' as const, count: usCounts.removed },
  ];

  const typeMap = new Map<string, number>();
  all.filter((n) => n.id !== tree.id).forEach((n) => {
    typeMap.set(n.workItemType, (typeMap.get(n.workItemType) ?? 0) + 1);
  });
  const typeCounts = Array.from(typeMap.entries())
    .map(([type, count]) => ({ type, count }))
    .sort((a, b) => b.count - a.count);

  // Split "done" story points into Closed (signed off) vs Resolved (built, awaiting sign-off)
  // so the page can show all three: Implemented (= Closed + Resolved), Closed, Resolved.
  let spClosed = 0;
  let spResolved = 0;
  let spRemaining = 0;
  all.forEach((n) => {
    const sp = n.storyPoints ?? 0;
    if (sp <= 0) return;
    if (n.workItemType === 'Feature' || n.workItemType === 'Epic') return;
    const c = classify(n.state);
    if (c === 'removed') return;
    if (c === 'closed') spClosed += sp;
    else if (c === 'resolved') spResolved += sp;
    else spRemaining += sp;
  });
  const spDone = spClosed + spResolved;

  const featuresDone = features.filter((f) => {
    const active = f.total - f.removed;
    return active > 0 && (f.closed + f.resolved) === active;
  }).length;
  const featuresTotal = features.length;
  const usDone = usCounts.closed + usCounts.resolved;
  const usTotal = allStories.length - usCounts.removed;
  const spTotal = spDone + spRemaining;

  // Single shared completion definition: count of user stories that are Implemented (Resolved +
  // Closed) over all NON-Removed user stories. The epics list (server GetChildCountsAsync) uses the
  // SAME formula, so the two screens can never disagree. Story points are still surfaced in the KPI
  // tile below, but do NOT weight the headline percentage — SP are frequently unpopulated, which
  // would otherwise make the figure inaccurate.
  const pct = (doneUs: number) => (usTotal > 0 ? Math.round((doneUs / usTotal) * 100) : 0);
  const percentComplete = pct(usDone);                    // Implemented = Resolved + Closed
  const percentResolved = pct(usCounts.resolved);
  const percentClosed = pct(usCounts.closed);

  return {
    features, storyStateCounts, typeCounts,
    spDone, spClosed, spResolved, spRemaining, spTotal,
    usDone, usClosed: usCounts.closed, usResolved: usCounts.resolved, usTotal,
    featuresDone, featuresTotal,
    percentComplete, percentResolved, percentClosed,
    progress: weightedProgress(allStories),
  };
}

// ── Per-story completion ──
export interface StoryStats { taskCount: number; taskDone: number; completePct: number; }
export function storyStats(story: EpicTreeNode): StoryStats {
  const tasks = flatten(story).filter((n) => n.workItemType === 'Task');
  const taskDone = tasks.filter((t) => isDone(classify(t.state))).length;
  const c = classify(story.state);
  let pct = 0;
  if (c === 'closed' || c === 'resolved') pct = 100;
  else if (tasks.length > 0) pct = Math.round((taskDone / tasks.length) * 100);
  return { taskCount: tasks.length, taskDone, completePct: pct };
}

export function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>').replace(/&#39;|&apos;/g, "'").replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// ── Status → brand colour + label (charts, pills, bars all agree) ──
export const STATUS_META: Record<Completion, { label: string; color: string }> = {
  closed: { label: 'Closed', color: brandColors.blue },
  resolved: { label: 'Resolved', color: brandColors.purple },
  inProgress: { label: 'Active', color: brandColors.green },
  toDo: { label: 'New', color: brandColors.lightBlue },
  removed: { label: 'Removed', color: brandColors.greyText },
};
export const STACK_ORDER: Completion[] = ['closed', 'resolved', 'inProgress', 'toDo', 'removed'];
const STATE_LABEL_COLOR: Record<StoryStateLabel, string> = {
  New: brandColors.lightBlue, Active: brandColors.green, Resolved: brandColors.purple,
  Closed: brandColors.blue, Removed: brandColors.greyText,
};

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXL, width: '100%', fontFamily: brandFonts.body },

  // ── Hero ──
  hero: {
    backgroundColor: brandColors.navy, color: brandColors.white,
    borderRadius: tokens.borderRadiusXLarge, padding: '2.125rem 2.375rem 2rem',
    boxShadow: tokens.shadow28, overflow: 'hidden',
  },
  kicker: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, color: brandColors.lightBlue,
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: tokens.spacingVerticalM,
  },
  heroTitleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalL, flexWrap: 'wrap' },
  heroTitle: {
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeHero800, lineHeight: tokens.lineHeightHero800,
    fontWeight: tokens.fontWeightBold, letterSpacing: '-0.01em', margin: 0,
  },
  detailMeta: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flexWrap: 'wrap',
    marginTop: tokens.spacingVerticalM, color: 'rgba(255,255,255,0.7)',
    fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase200,
  },
  heroGrid: {
    display: 'grid', gridTemplateColumns: '1.45fr 1fr', gap: tokens.spacingHorizontalXXXL, alignItems: 'center',
    marginTop: tokens.spacingVerticalXXL,
    '@media (max-width: 860px)': { gridTemplateColumns: '1fr', rowGap: tokens.spacingVerticalXL },
  },
  heroBars: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  barRow: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  barTop: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: tokens.spacingHorizontalM },
  barLabel: { fontSize: tokens.fontSizeBase300, color: 'rgba(255,255,255,0.92)' },
  barVal: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase200, color: 'rgba(255,255,255,0.6)' },
  heroTrack: { height: '0.375rem', borderRadius: tokens.borderRadiusCircular, backgroundColor: 'rgba(255,255,255,0.12)', overflow: 'hidden' },
  heroFill: { height: '100%', borderRadius: tokens.borderRadiusCircular },
  composite: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: tokens.spacingVerticalXS,
    borderLeft: '1px solid rgba(255,255,255,0.14)', paddingLeft: tokens.spacingHorizontalXXXL,
    '@media (max-width: 860px)': { borderLeft: 'none', paddingLeft: 0 },
  },
  compositeLabel: {
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.14em', textTransform: 'uppercase', color: brandColors.lightBlue,
  },
  compositeValue: { display: 'flex', alignItems: 'baseline', gap: tokens.spacingHorizontalXS },
  compositeNum: { fontFamily: brandFonts.heading, fontSize: '3.5rem', fontWeight: tokens.fontWeightBold, lineHeight: '1', color: brandColors.white },
  compositePct: { fontFamily: brandFonts.heading, fontSize: tokens.fontSizeHero700, fontWeight: tokens.fontWeightSemibold, color: brandColors.lightBlue },
  compositeMeta: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase200, color: 'rgba(255,255,255,0.6)' },

  // ── Three-metric progress block (Implemented / Closed / Resolved) ──
  metricLabelRow: { display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  metricRow: {
    display: 'flex', gap: tokens.spacingHorizontalXXL, marginTop: tokens.spacingVerticalL,
    paddingTop: tokens.spacingVerticalL, borderTop: '1px solid rgba(255,255,255,0.14)',
  },
  metricSecondary: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: tokens.spacingVerticalXXS },
  metricSubLabel: {
    display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalS,
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase100, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.78)',
  },
  metricValueSm: { display: 'flex', alignItems: 'baseline', gap: tokens.spacingHorizontalXXS },
  metricNumSm: { fontFamily: brandFonts.heading, fontSize: tokens.fontSizeHero700, fontWeight: tokens.fontWeightBold, lineHeight: '1', color: brandColors.white },
  metricPctSm: { fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold, color: 'rgba(255,255,255,0.7)' },
  metricMetaSm: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase100, color: 'rgba(255,255,255,0.55)' },
  infoIcon: {
    // Light-gray (i) button on the navy hero; brightens to white on hover/focus.
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 0,
    padding: 0, border: 'none', backgroundColor: 'transparent',
    color: '#C8C8C8', cursor: 'pointer',
    '&:hover': { color: brandColors.white },
    '&:focus-visible': { outline: `2px solid ${brandColors.lightBlue}`, outlineOffset: '2px', borderRadius: tokens.borderRadiusSmall },
  },

  // ── Transformr dialog standard (dialog-standard.context.md, locked 2026-06-07) ──
  // Info variant: head → body → foot shell + brand chrome. List-specific keys (search band,
  // list-meta, list scroll, rows, four-state pane) are omitted — this dialog has no list; it
  // sizes to its single explanatory paragraph rather than forcing a 24rem scroll body.
  dlgSurface: { padding: 0, maxWidth: '34rem', width: '100%', overflow: 'hidden' },
  dlgShell: { display: 'flex', flexDirection: 'column', maxHeight: '85vh' },
  dlgHead: {
    position: 'relative',
    padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalXXL} ${tokens.spacingVerticalL}`,
    backgroundColor: brandColors.white,
    borderBottom: `1px solid ${brandColors.greyLine}`,
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, flexShrink: 0,
  },
  dlgKicker: {
    display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalS,
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase100, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.18em', textTransform: 'uppercase', color: brandColors.greyText,
  },
  dlgKickerRule: {
    display: 'inline-block', width: '2rem', height: '2px',
    backgroundColor: brandColors.blue, borderRadius: '1px', flexShrink: 0,
  },
  dlgTitle: {
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeHero700, lineHeight: tokens.lineHeightHero700,
    fontWeight: tokens.fontWeightSemibold, color: brandColors.navy, letterSpacing: '-0.02em', margin: 0,
    paddingRight: tokens.spacingHorizontalXXL, // clear the close X
  },
  dlgClose: {
    position: 'absolute', top: tokens.spacingVerticalL, right: tokens.spacingHorizontalL,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: '2rem', height: '2rem', padding: 0, border: 'none', backgroundColor: 'transparent',
    borderRadius: tokens.borderRadiusMedium, cursor: 'pointer', color: brandColors.greyText,
    '&:hover': { color: brandColors.navy, backgroundColor: tokens.colorNeutralBackground1Hover },
    '&:focus-visible': { outline: `2px solid ${brandColors.blue}`, outlineOffset: '1px' },
  },
  dlgBody: {
    flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, backgroundColor: brandColors.white,
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    paddingTop: tokens.spacingVerticalL, paddingBottom: tokens.spacingVerticalL,
  },
  dlgInfoText: { color: tokens.colorNeutralForeground1, lineHeight: tokens.lineHeightBase400 },
  dlgFoot: {
    display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: tokens.spacingHorizontalL,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXXL}`,
    borderTop: `1px solid ${brandColors.greyLine}`, backgroundColor: brandColors.white, flexShrink: 0,
  },

  // ── Pills ──
  pill: {
    display: 'inline-flex', alignItems: 'center', borderRadius: tokens.borderRadiusCircular,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase100, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap',
  },
  heroPill: {
    backgroundColor: 'rgba(255,255,255,0.14)', color: brandColors.white,
    display: 'inline-flex', alignItems: 'center', borderRadius: tokens.borderRadiusCircular,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap',
  },

  // ── KPI tiles ──
  kpis: {
    display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: tokens.spacingHorizontalL,
    '@media (max-width: 1080px)': { gridTemplateColumns: 'repeat(3, 1fr)' },
    '@media (max-width: 860px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
  },
  kpi: {
    backgroundColor: brandColors.white,
    ...shorthands.border('1px', 'solid', brandColors.greyLine),
    // Thick brand-blue left edge (per the design's .dva-kpi).
    ...shorthands.borderLeft('3px', 'solid', brandColors.blue),
    borderRadius: tokens.borderRadiusMedium,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS,
  },
  kpiLabel: {
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase100, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.12em', textTransform: 'uppercase', color: brandColors.greyText,
  },
  kpiVal: { display: 'flex', alignItems: 'baseline', gap: tokens.spacingHorizontalXS },
  kpiNum: { fontFamily: brandFonts.heading, fontSize: '2.375rem', fontWeight: tokens.fontWeightSemibold, color: brandColors.navy, lineHeight: '1', letterSpacing: '-0.02em' },
  kpiUnit: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase300, color: brandColors.greyText },
  // ── Detail grid ──
  detailGrid: {
    // Chart 65% / story list 35%. minmax(0, …) (not bare fr) lets the chart column shrink
    // below its content width so the chart scrolls inside its card instead of blowing the
    // grid out past the page (fr tracks have an automatic min-content minimum).
    display: 'grid', gridTemplateColumns: 'minmax(0, 13fr) minmax(0, 7fr)', gap: tokens.spacingHorizontalXL, alignItems: 'start',
    '@media (max-width: 1080px)': { gridTemplateColumns: '1fr' },
  },
  // Left column wrapper — stacks the feature chart and the new-scope card beneath it.
  leftCol: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXL, minWidth: 0 },
  card: {
    backgroundColor: brandColors.white, border: `1px solid ${brandColors.greyLine}`,
    borderRadius: tokens.borderRadiusLarge,
    padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalXL}`,
    // min-width:0 lets this grid item shrink to its track so the chart's overflowX can engage.
    minWidth: 0,
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL,
  },
  cardTitle: { fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold, color: brandColors.navy, margin: 0 },
  cardHint: { color: brandColors.greyText },

  // ── Stacked column chart — taller plot, labels under each column (as before) ──
  // When there are many features, columns keep their width and the chart scrolls
  // horizontally inside this box rather than squashing every column off the page.
  // overflowX:auto pairs with overflowY:hidden (avoids the CSS visible→auto coupling that
  // would add a vertical scrollbar); nothing important is clipped vertically because the
  // hover card is anchored at the TOP of each full-height column, inside the box.
  chart: {
    display: 'flex', alignItems: 'flex-end', gap: tokens.spacingHorizontalM, height: '22rem',
    paddingTop: tokens.spacingVerticalM, overflowX: 'auto', overflowY: 'hidden',
  },
  col: {
    // flex-grow 1 so a few columns still spread to fill the chart; flex-shrink 0 + a fixed
    // basis/min-width so many columns never compress below a legible width (→ scroll instead).
    position: 'relative', flex: '1 0 3rem', minWidth: '3rem', display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: tokens.spacingVerticalXS, height: '100%', justifyContent: 'flex-end', cursor: 'pointer',
    '&:hover [role="tooltip"], &:focus-within [role="tooltip"]': { opacity: 1, visibility: 'visible' },
  },
  colTrack: { width: '100%', maxWidth: '2.5rem', display: 'flex', flexDirection: 'column-reverse', borderRadius: tokens.borderRadiusSmall, overflow: 'hidden', backgroundColor: tokens.colorNeutralBackground3 },
  colSeg: { width: '100%' },
  // Hover card over a bar (revealed on hover / keyboard focus).
  hoverCard: {
    // Anchored to the TOP of the (full-height) column so it stays inside the chart's
    // overflow box — see the chart/col notes above re: horizontal scroll.
    position: 'absolute', top: tokens.spacingVerticalS, left: '50%', transform: 'translateX(-50%)',
    zIndex: 9999, minWidth: '12rem', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    backgroundColor: brandColors.navy, color: brandColors.white,
    // Subtle external border, slightly lighter than the navy card surface.
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: tokens.borderRadiusMedium, boxShadow: tokens.shadow16,
    opacity: 0, visibility: 'hidden', transitionProperty: 'opacity', transitionDuration: '0.12s', pointerEvents: 'none',
  },
  hoverTitle: { fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightSemibold, lineHeight: tokens.lineHeightBase300 },
  hoverCode: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase100, color: 'rgba(255,255,255,0.7)' },
  hoverRows: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, marginTop: tokens.spacingVerticalXS },
  hoverRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS, fontSize: tokens.fontSizeBase200 },
  hoverCount: { marginLeft: 'auto', fontFamily: brandFonts.mono, color: 'rgba(255,255,255,0.85)' },
  colLabel: {
    fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase100, color: brandColors.greyText,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', textAlign: 'center',
  },
  colSelected: { outline: `2px solid ${brandColors.blue}`, outlineOffset: '2px', borderRadius: tokens.borderRadiusSmall },
  legend: { display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: tokens.spacingHorizontalL, marginTop: tokens.spacingVerticalS },
  legendItem: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS, color: brandColors.greyText, fontSize: tokens.fontSizeBase200 },
  swatch: { width: '0.625rem', height: '0.625rem', borderRadius: tokens.borderRadiusSmall, flexShrink: 0 },

  // ── Right panel ──
  panelHead: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: tokens.spacingHorizontalM },
  panelTitle: { fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold, color: brandColors.navy, margin: 0 },
  panelLink: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase200, display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXXS, color: brandColors.blue, flexShrink: 0 },
  sectionLabel: { fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase100, fontWeight: tokens.fontWeightSemibold, letterSpacing: '0.12em', textTransform: 'uppercase', color: brandColors.greyText },
  desc: { color: tokens.colorNeutralForeground1, whiteSpace: 'pre-line', lineHeight: tokens.lineHeightBase400 },
  storiesHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  backBtn: { alignSelf: 'flex-start' },

  story: {
    border: `1px solid ${brandColors.greyLine}`, borderRadius: tokens.borderRadiusMedium,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS,
  },
  storyTop: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: tokens.spacingHorizontalM },
  storyTitle: { fontFamily: brandFonts.heading, fontWeight: tokens.fontWeightSemibold, color: brandColors.navy, minWidth: 0 },
  storyTrack: { width: '100%', height: '0.3125rem', borderRadius: tokens.borderRadiusCircular, overflow: 'hidden', backgroundColor: brandColors.greyLine },
  storyFill: { height: '100%', borderRadius: tokens.borderRadiusCircular },
  storyFoot: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase100, color: brandColors.greyText },
  storyList: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  clickableStory: { cursor: 'pointer', '&:hover': { border: `1px solid ${brandColors.blue}`, backgroundColor: tokens.colorNeutralBackground1Hover } },

  workItemLink: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase100, display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXXS, color: brandColors.blue },

  storyDesc: { color: tokens.colorNeutralForeground2, lineHeight: tokens.lineHeightBase300, whiteSpace: 'pre-line' },
  showMore: {
    backgroundColor: 'transparent', border: 'none', padding: 0, marginLeft: tokens.spacingHorizontalXXS,
    cursor: 'pointer', color: brandColors.blue, fontFamily: brandFonts.body,
    fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightRegular,
  },
  storySearch: { width: '100%' },

  // Status filter chips
  filterRow: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalXS },
  chip: {
    display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXXS, cursor: 'pointer',
    borderRadius: tokens.borderRadiusCircular, padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    border: `1px solid ${brandColors.greyLine}`, backgroundColor: brandColors.white, color: brandColors.greyText,
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase100, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.04em', textTransform: 'uppercase',
  },
  chipActive: { backgroundColor: brandColors.navy, color: brandColors.white, border: `1px solid ${brandColors.navy}` },

  emptyOrError: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: tokens.spacingVerticalS, padding: tokens.spacingVerticalXXXL, textAlign: 'center', color: tokens.colorNeutralForeground2,
  },
});

type Styles = ReturnType<typeof useStyles>;

function WorkItemLink({ url, id, styles }: { url: string | null; id: number; styles: Styles }) {
  if (!url) return null;
  return (
    <Link className={styles.workItemLink} href={url} target="_blank" rel="noopener noreferrer"
      aria-label={`Open work item ${id} in Azure DevOps (opens in a new tab)`}>
      #{id} <OpenRegular fontSize={13} />
    </Link>
  );
}

function SoftPill({ state, styles }: { state: string; styles: Styles }) {
  const c = STATUS_META[classify(state)].color;
  return <span className={styles.pill} style={{ backgroundColor: `${c}29`, color: c }}>{(state || 'Unknown').trim()}</span>;
}

// Story card — customer description with show more/less after 120 chars, a
// completion bar, and a deep link to the work item.
const DESC_LIMIT = 120;
function StoryCard({ story, styles }: { story: EpicTreeNode; styles: Styles }) {
  const [expanded, setExpanded] = useState(false);
  const title = story.customerTitle?.trim() || story.title;
  const desc = stripHtml(story.customerDescription || story.description);
  const long = desc.length > DESC_LIMIT;
  const shown = !long || expanded ? desc : `${desc.slice(0, DESC_LIMIT).trimEnd()}…`;
  const st = storyStats(story);
  const c = STATUS_META[classify(story.state)].color;
  return (
    <div className={styles.story}>
      <div className={styles.storyTop}>
        <span className={styles.storyTitle}>{title}</span>
        <SoftPill state={story.state} styles={styles} />
      </div>
      {desc ? (
        <Caption1 className={styles.storyDesc}>
          {shown}
          {long && (
            <button type="button" className={styles.showMore} onClick={() => setExpanded((e) => !e)}>
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </Caption1>
      ) : null}
      <div className={styles.storyTrack} role="img" aria-label={`${st.completePct}% complete`}>
        <div className={styles.storyFill} style={{ width: `${st.completePct}%`, backgroundColor: c }} />
      </div>
      <div className={styles.storyFoot}>
        <span>{st.completePct}% · {st.taskCount > 0 ? `${st.taskDone}/${st.taskCount} tasks` : 'no tasks'}</span>
        <WorkItemLink url={story.url} id={story.id} styles={styles} />
      </div>
    </div>
  );
}

// CSS stacked-column chart: one column per feature, segments by story status.
// Each column is clickable (loads that feature's stories) and shows a hover card
// with the feature's per-status breakdown. The focused feature is highlighted.
function FeatureChart({ features, selectedId, onSelect, styles }: {
  features: FeatureRow[]; selectedId: number | null; onSelect: (id: number) => void; styles: Styles;
}) {
  const maxTotal = Math.max(1, ...features.map((f) => f.total));
  return (
    <>
      <div className={styles.chart}>
        {features.map((f) => {
          const colHeightPct = (f.total / maxTotal) * 100;
          const active = f.total - f.removed;
          const pct = active > 0 ? Math.round(((f.closed + f.resolved) / active) * 100) : 0;
          return (
            <div
              key={f.id}
              className={mergeClasses(styles.col, selectedId === f.id && styles.colSelected)}
              onClick={() => onSelect(f.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(f.id); } }}
              role="button" tabIndex={0}
              aria-label={`${f.code}: ${f.total} stories, ${pct}% complete`}
            >
              {/* hover card */}
              <div className={styles.hoverCard} role="tooltip">
                <span className={styles.hoverTitle}>{f.customerTitle?.trim() || f.name}</span>
                <span className={styles.hoverCode}>{f.code} · {pct}% complete · {f.total} stories</span>
                <div className={styles.hoverRows}>
                  {STACK_ORDER.map((k) => (f[k] ? (
                    <div key={k} className={styles.hoverRow}>
                      <span className={styles.swatch} style={{ backgroundColor: STATUS_META[k].color }} aria-hidden />
                      <span>{STATUS_META[k].label}</span>
                      <span className={styles.hoverCount}>{f[k]}</span>
                    </div>
                  ) : null))}
                </div>
              </div>
              <div className={styles.colTrack} style={{ height: `${colHeightPct}%` }}>
                {STACK_ORDER.map((k) => {
                  const n = f[k];
                  if (!n) return null;
                  return <div key={k} className={styles.colSeg} style={{ height: `${(n / f.total) * 100}%`, backgroundColor: STATUS_META[k].color }} />;
                })}
              </div>
              <span className={styles.colLabel}>{f.code}</span>
            </div>
          );
        })}
      </div>
      <div className={styles.legend}>
        {STACK_ORDER.map((k) => (
          <span key={k} className={styles.legendItem}>
            <span className={styles.swatch} style={{ backgroundColor: STATUS_META[k].color }} aria-hidden />
            {STATUS_META[k].label}
          </span>
        ))}
      </div>
    </>
  );
}

export function EpicProgressPage() {
  const styles = useStyles();
  const { projectId, epicId } = useParams<{ projectId: string; epicId: string }>();
  const epicIdNum = epicId ? parseInt(epicId, 10) : undefined;

  const { data, isLoading, error, refetch } = useEpicTree(projectId, epicIdNum);
  const { data: projects } = useMyProjects();
  const project = projects?.find((p) => p.id === projectId);

  const r = useMemo(() => (data ? rollup(data) : null), [data]);

  const [statusFilter, setStatusFilter] = useState<Completion | 'all'>('all');
  const [selectedFeatureId, setSelectedFeatureId] = useState<number | null>(null);
  const [storySearch, setStorySearch] = useState('');
  useEffect(() => { setSelectedFeatureId(null); setStatusFilter('all'); setStorySearch(''); }, [epicIdNum]);
  // Switching the focused feature resets the status filter + search.
  useEffect(() => { setStatusFilter('all'); setStorySearch(''); }, [selectedFeatureId]);

  // Features alphabetical by technical title, then id (tie-break).
  const sortedFeatures = useMemo(
    () => (r ? [...r.features].sort((a, b) => a.name.localeCompare(b.name) || (a.id - b.id)) : []),
    [r],
  );
  // Default focus = first feature in the chart.
  const focusedFeatureId = selectedFeatureId ?? sortedFeatures[0]?.id ?? null;
  const focusedFeature = useMemo(
    () => (data && focusedFeatureId != null ? flatten(data).find((n) => n.id === focusedFeatureId) ?? null : null),
    [data, focusedFeatureId],
  );

  if (isLoading) return <div className={styles.emptyOrError}><Spinner size="small" label="Loading epic progress…" /></div>;
  if (error) {
    return (
      <div className={styles.emptyOrError}>
        <Body1>Couldn't load epic progress.</Body1>
        <Caption1>{(error as Error).message}</Caption1>
        <Button appearance="primary" onClick={() => refetch()}>Try again</Button>
      </div>
    );
  }
  if (!data || !r) return <div className={styles.emptyOrError}><Body1>Epic not found.</Body1></div>;

  const d = discoveryStats(data);
  const quality = qualityStats(data);
  const totalStories = r.storyStateCounts.reduce((acc, s) => acc + s.count, 0);
  const heroBars = r.storyStateCounts.filter((s) => s.count > 0);

  // Story list is scoped to the focused feature (default = first feature in the chart).
  const featureDesc = focusedFeature ? stripHtml(focusedFeature.customerDescription || focusedFeature.description) : '';
  const baseStories = focusedFeature
    ? flatten(focusedFeature).filter((n) => n.workItemType === 'User Story' && n.id !== focusedFeature.id)
    : [];
  // Status filter chips (only statuses actually present in the focused feature).
  const statusOptions: { key: Completion | 'all'; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: baseStories.length },
    ...STACK_ORDER
      .map((k) => ({ key: k, label: STATUS_META[k].label, count: baseStories.filter((s) => classify(s.state) === k).length }))
      .filter((o) => o.count > 0),
  ];
  const q = storySearch.trim().toLowerCase();
  const visibleStories = baseStories
    .filter((s) => statusFilter === 'all' || classify(s.state) === statusFilter)
    .filter((s) => !q
      || (s.customerTitle ?? s.title).toLowerCase().includes(q)
      || stripHtml(s.customerDescription || s.description).toLowerCase().includes(q))
    .slice()
    .sort((a, b) => Number(isRemoved(a.state)) - Number(isRemoved(b.state)));

  return (
    <div className={styles.page}>
      {/* Navy hero */}
      <section className={styles.hero}>
        <div className={styles.kicker}><DataTrendingRegular fontSize={14} aria-hidden /> Epic Progress</div>
        <div className={styles.heroTitleRow}>
          <h1 className={styles.heroTitle}>{data.customerTitle?.trim() || data.title}</h1>
          <span className={styles.heroPill}>{(data.state || 'Unknown').trim()}</span>
        </div>
        <div className={styles.detailMeta}>
          {project ? <span>{project.name}</span> : null}
          {data.customerTitle?.trim() ? <span>· {data.title}</span> : null}
          <span>· {r.featuresTotal} feature{r.featuresTotal === 1 ? '' : 's'}</span>
          <span>· {totalStories} stor{totalStories === 1 ? 'y' : 'ies'}</span>
          {data.storyPoints ? <span>· {data.storyPoints} epic SP</span> : null}
        </div>

        <div className={styles.heroGrid}>
          <div className={styles.heroBars}>
            {heroBars.map((b) => (
              <div key={b.state} className={styles.barRow}>
                <div className={styles.barTop}>
                  <span className={styles.barLabel}>{b.state}</span>
                  <span className={styles.barVal}>{b.count} · {Math.round((b.count / (totalStories || 1)) * 100)}%</span>
                </div>
                <div className={styles.heroTrack}>
                  <div className={styles.heroFill} style={{ width: `${(b.count / (totalStories || 1)) * 100}%`, backgroundColor: STATE_LABEL_COLOR[b.state] }} />
                </div>
              </div>
            ))}
            {heroBars.length === 0 && <span className={styles.barLabel}>No stories under this epic yet.</span>}
          </div>
          <HeroProgressMetrics
            percentProgress={r.progress.percent}
            percentClosed={r.percentClosed}
            percentResolved={r.percentResolved}
            progressMeta={progressBasisLabel(r.progress)}
            closedMeta={`${r.usClosed} / ${r.usTotal} stories`}
            resolvedMeta={`${r.usResolved} / ${r.usTotal} stories`}
            unit="user stories"
          />
        </div>
      </section>

      {/* KPI tiles */}
      <div className={styles.kpis}>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Features complete</span>
          <div className={styles.kpiVal}><span className={styles.kpiNum}>{r.featuresDone}</span><span className={styles.kpiUnit}>/ {r.featuresTotal}</span></div>
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Stories complete</span>
          <div className={styles.kpiVal}><span className={styles.kpiNum}>{r.usDone}</span><span className={styles.kpiUnit}>/ {r.usTotal}</span></div>
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Story points</span>
          <div className={styles.kpiVal}><span className={styles.kpiNum}>{r.spDone}</span><span className={styles.kpiUnit}>/ {r.spTotal}</span></div>
        </div>
        {/* No "Implemented %" tile: it was usDone/usTotal — the same ratio as "Stories complete"
            above — and a second completion % competing with the hero's Progress figure. */}
        {/* Quality signals (#13419) — open items still to be worked through before sign-off. */}
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Open UAT items</span>
          <div className={styles.kpiVal}><span className={styles.kpiNum}>{quality.openUat}</span><span className={styles.kpiUnit}>tagged uat</span></div>
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Open bugs</span>
          <div className={styles.kpiVal}><span className={styles.kpiNum}>{quality.openBug}</span><span className={styles.kpiUnit}>not closed</span></div>
        </div>
      </div>

      {/* Detail grid */}
      <div className={styles.detailGrid}>
        {/* Left — feature chart, with the new-scope card underneath it */}
        <div className={styles.leftCol}>
          <section className={styles.card} aria-label="Stories by feature and status">
            <h2 className={styles.cardTitle}>Stories by feature</h2>
            {sortedFeatures.length === 0 ? (
              <Caption1 className={styles.cardHint}>No features under this epic.</Caption1>
            ) : (
              <>
                <Caption1 className={styles.cardHint}>Each column is a feature, split by story status. Select one to load its stories.</Caption1>
                <FeatureChart features={sortedFeatures} selectedId={focusedFeatureId} onSelect={setSelectedFeatureId} styles={styles} />
              </>
            )}
          </section>

          {/* New scope — discovery-new work added after sign-off (Doc 29) */}
          <section className={styles.card} aria-label="New scope">
            <h2 className={styles.cardTitle}>New Scope</h2>
            <Caption1 className={styles.cardHint}>
              Work added <strong>after the spec was signed off</strong> — scope legitimately needed to
              build the solution properly — as a share of all work in this epic.
              {d.newCount === 0 ? ' None recorded yet.' : ''}
            </Caption1>
            <div className={styles.kpiVal}>
              <span className={styles.kpiNum}>{d.newPct}</span>
              <span className={styles.kpiUnit}>% · {d.newCount} of {d.total} work items</span>
            </div>
          </section>
        </div>

        {/* Right — focused feature: overview + searchable, status-filtered story list */}
        <section className={styles.card} aria-label="User stories">
          {focusedFeature ? (
            <>
              <div className={styles.panelHead}>
                <h2 className={styles.panelTitle}>{focusedFeature.customerTitle?.trim() || focusedFeature.title}</h2>
                <WorkItemLink url={focusedFeature.url} id={focusedFeature.id} styles={styles} />
              </div>
              {featureDesc ? (
                <>
                  <span className={styles.sectionLabel}>Overview</span>
                  <Body1 className={styles.desc}>{featureDesc}</Body1>
                </>
              ) : null}
              <SearchBox
                appearance="underline"
                className={styles.storySearch}
                placeholder="Search stories…"
                value={storySearch}
                onChange={(_e, d) => setStorySearch(d.value)}
                aria-label="Search user stories"
              />
              <div className={styles.filterRow} role="group" aria-label="Filter stories by status">
                {statusOptions.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    className={mergeClasses(styles.chip, statusFilter === o.key && styles.chipActive)}
                    onClick={() => setStatusFilter(o.key)}
                    aria-pressed={statusFilter === o.key}
                  >
                    {o.label} {o.count}
                  </button>
                ))}
              </div>
              <div className={styles.storiesHead}>
                <span className={styles.sectionLabel}>User stories</span>
                <Caption1 className={styles.cardHint}>{visibleStories.length}</Caption1>
              </div>
              <div className={styles.storyList}>
                {visibleStories.map((s) => <StoryCard key={s.id} story={s} styles={styles} />)}
                {visibleStories.length === 0 && (
                  <Caption1 className={styles.cardHint}>
                    {storySearch || statusFilter !== 'all' ? 'No stories match.' : 'No user stories under this feature.'}
                  </Caption1>
                )}
              </div>
            </>
          ) : (
            <Caption1 className={styles.cardHint}>No features under this epic.</Caption1>
          )}
        </section>
      </div>
    </div>
  );
}
